// @spec DHB-STR-002
// @spec DHB-STR-004
// @spec DHB-STR-005
// @spec DHB-STR-006
// @spec DHB-STR-036
// @spec DHB-STR-037
// @spec DHB-STR-038
// @spec DHB-STR-039
'use client';

import { useUser } from '@clerk/clerk-react';
import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { ArrowLeft, AlertCircle, Ban, Database, Network } from 'lucide-react';
import { useState } from 'react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  AccessControlSection,
  DatapointDetailAudit,
  DatapointDetailHeader,
  DatapointDetailLineage,
  DatapointDetailMetadata,
  DatapointDetailRegulations,
  DatapointDetailSkeleton,
  // DatapointDetailValidation, // Temporarily disabled
  EditDatapointDialog,
  useDatapoint,
} from '@/features/datahub';
import { useOrganizationId } from '@/hooks/use-organization-id';
import { useOrgRole } from '@/hooks/use-org-role';
import i18n from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { ApiClientError } from '@/types/api';
import { useTranslation } from 'react-i18next';

type DetailTab = 'details' | 'lineage';

export const Route = createFileRoute('/_authenticated/datahub/datapoints/$datapointId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.datapointDetails'),
  }),
  component: DatapointLayout,
});

/**
 * Layout component that serves as parent for both the detail page
 * and child routes (e.g., /entry).
 */
function DatapointLayout() {
  const { datapointId } = Route.useParams();
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;
  const isChildRouteActive = currentPath.includes(`/datapoints/${datapointId}/`);

  if (isChildRouteActive) {
    return <Outlet />;
  }

  return <DatapointDetailPage />;
}

/**
 * Datapoint detail page - Full datapoint definition view.
 *
 * Displays complete datapoint information including:
 * - Name, code, and status
 * - Data type and unit configuration
 * - Validation rules
 * - Regulatory mappings
 * - Ownership and timestamps
 */
function DatapointDetailPage() {
  const { t } = useTranslation('common');
  const { datapointId } = Route.useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { isAdmin } = useOrgRole();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<DetailTab>('details');

  const { datapoint, isLoading, isError, error, refetch } = useDatapoint({
    datapointId,
  });

  // Get organization ID for lineage component. Canonical hook so the
  // backend-authoritative active-org mapping wins over stale Clerk
  // `publicMetadata.internalId` (Subsidiary Refactor WP0-E / WP4-E).
  const organizationId = useOrganizationId();

  // Get internal user ID for owner check
  const internalUserId = (user?.publicMetadata as { internalId?: string } | null)?.internalId;

  // Check if current user is the owner
  const isOwner = !!(internalUserId && datapoint?.ownerId === internalUserId);

  // Handle back navigation
  const handleBack = () => {
    void navigate({ to: '/datahub/datapoints' });
  };

  // Determine error type for appropriate UI
  const isNotFound = error instanceof ApiClientError && error.status === 404;
  const isForbidden = error instanceof ApiClientError && error.status === 403;

  return (
    <div className="p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        {/* Breadcrumb navigation */}
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/datahub/datapoints">Data Hub</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/datahub/datapoints">Datapoints</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{datapoint?.name ?? 'Datapoint Details'}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Back button */}
        <Button variant="ghost" size="sm" onClick={handleBack} className="-ml-2">
          <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
          Back to Catalog
        </Button>

        {/* Loading state */}
        {isLoading && <DatapointDetailSkeleton />}

        {/* Not found error */}
        {isNotFound && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Datapoint not found</AlertTitle>
            <AlertDescription>
              The datapoint you are looking for does not exist or has been deleted.
            </AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleBack}>
              Return to Catalog
            </Button>
          </Alert>
        )}

        {/* Forbidden error */}
        {isForbidden && (
          <Alert variant="destructive">
            <Ban className="h-4 w-4" />
            <AlertTitle>Access denied</AlertTitle>
            <AlertDescription>You do not have permission to view this datapoint.</AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={handleBack}>
              Return to Catalog
            </Button>
          </Alert>
        )}

        {/* Generic error */}
        {isError && !isNotFound && !isForbidden && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('common:routeErrors.errorLoadingDatapoint')}</AlertTitle>
            <AlertDescription>
              {error instanceof Error ? error.message : 'Failed to load datapoint details'}
            </AlertDescription>
            <Button variant="outline" size="sm" className="mt-4" onClick={() => void refetch()}>
              Try again
            </Button>
          </Alert>
        )}

        {/* Datapoint content */}
        {datapoint && !isLoading && (
          <div className="space-y-6">
            <DatapointDetailHeader
              datapoint={datapoint}
              onEditClick={() => {
                setIsEditDialogOpen(true);
              }}
              isAdmin={isAdmin}
            />

            {/* Tab navigation */}
            <div className="flex gap-1 rounded-lg border bg-muted p-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setActiveTab('details');
                }}
                className={cn('flex-1 gap-2', activeTab === 'details' && 'bg-background shadow-sm')}
              >
                <Database className="h-4 w-4" aria-hidden="true" />
                Details
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setActiveTab('lineage');
                }}
                className={cn('flex-1 gap-2', activeTab === 'lineage' && 'bg-background shadow-sm')}
              >
                <Network className="h-4 w-4" aria-hidden="true" />
                Lineage
              </Button>
            </div>

            {/* Details tab content */}
            {activeTab === 'details' && (
              <>
                <DatapointDetailMetadata datapoint={datapoint} canEdit={isAdmin} />
                {/* Validation rules temporarily disabled
                <DatapointDetailValidation
                  datapointId={datapoint.id}
                  organizationId={datapoint.organizationId}
                  dataType={datapoint.dataType}
                  rules={datapoint.validationRules}
                  isReadOnly={!isAdmin}
                />
                */}
                <DatapointDetailRegulations
                  datapointId={datapoint.id}
                  mappings={datapoint.regulatoryMappings}
                  isReadOnly={!isAdmin}
                />
                {/* Access Control - shown to admins and owners */}
                {organizationId && (
                  <AccessControlSection
                    datapointId={datapoint.id}
                    organizationId={organizationId}
                    ownerId={datapoint.ownerId}
                    isOwner={isOwner}
                    isAdmin={isAdmin}
                  />
                )}
                {/* Change History - shown to admins and owners */}
                {(isAdmin || isOwner) && <DatapointDetailAudit datapointId={datapoint.id} />}
              </>
            )}

            {/* Lineage tab content */}
            {activeTab === 'lineage' && organizationId && (
              <DatapointDetailLineage datapointId={datapoint.id} organizationId={organizationId} />
            )}
          </div>
        )}

        {/* Edit Dialog */}
        {datapoint && (
          <EditDatapointDialog
            datapoint={datapoint}
            open={isEditDialogOpen}
            onOpenChange={setIsEditDialogOpen}
          />
        )}
      </div>
    </div>
  );
}
